import { useState, FormEvent } from 'react';
import Modal from '../../components/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../services/api';
import './LoginMenu.css';

interface LoginMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
}

/**
 * Login modal menu component
 * Handles user authentication with username and password
 * @param isOpen - Controls modal visibility
 * @param onClose - Callback when modal is closed
 * @param onSwitchToRegister - Callback to switch to register modal
 * @returns The rendered login modal component
 */
function LoginMenu({ isOpen, onClose, onSwitchToRegister }: LoginMenuProps): JSX.Element {
  const { login } = useAuth();
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  /**
   * Handles form submission for login
   * @param event - Form submit event
   */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(username, password);
      setUsername('');
      setPassword('');
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Resets form state when modal closes
   */
  const handleClose = (): void => {
    setUsername('');
    setPassword('');
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Login">
      <form className="login-form" onSubmit={handleSubmit}>
        {error && <div className="login-error">{error}</div>}

        <div className="login-field">
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isSubmitting}
            autoComplete="username"
          />
        </div>

        <div className="login-field">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            autoComplete="current-password"
          />
        </div>

        <button type="submit" className="login-submit" disabled={isSubmitting}>
          {isSubmitting ? 'Logging in...' : 'Login'}
        </button>

        <div className="login-switch">
          <span>No account? </span>
          <button type="button" className="login-switch-btn" onClick={onSwitchToRegister}>
            Register
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default LoginMenu;
